/* eslint-disable @next/next/no-img-element */
"use client";

import React, { useEffect, useState } from "react";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

/* ---------- Types ---------- */
interface TestimonialItem {
  _id: string;
  name: string;
  content: string; // HTML content
  rating: number;
  image?: string;
}

/* ---------- Component ---------- */
const Testimonial = () => {
  const [testimonials, setTestimonials] = useState<TestimonialItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);

  /* ---------- Fetch testimonials ---------- */
  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/testimonial`, {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        setTestimonials(data.data || []);
      })
      .catch((err) => {
        console.error("Testimonial fetch failed:", err);
      });
  }, []);

  /* ---------- Guards ---------- */
  if (!testimonials.length) {
    return null; // safe: data not loaded yet
  }

  const { image, name, content, rating } = testimonials[currentIndex];

  /* ---------- Slider logic ---------- */
  const handleChange = (nextIndex: number) => {
    setFade(false);
    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setFade(true);
    }, 300);
  };

  const handlePrev = () =>
    handleChange(
      currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1
    );

  const handleNext = () =>
    handleChange(
      currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1
    );

  /* ---------- UI ---------- */
  return (
    <section className="w-full bg-white py-20 px-6">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">
        
        {/* ---------- Image ---------- */}
        <div
          className={`flex-1 transition-all duration-500 ${
            fade ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-5"
          }`}
        >
          <img
            src={image || "/default-user.png"}
            alt={name}
            className="w-full max-w-[500px] h-auto rounded-2xl shadow-md"
          />
        </div>

        {/* ---------- Content ---------- */}
        <div
          className={`flex-1 transition-all duration-500 ${
            fade ? "opacity-100 translate-x-0" : "opacity-0 translate-x-5"
          }`}
        >
          <FaQuoteLeft className="text-[#212227] text-3xl mb-6" />

          {/* Render HTML safely */}
          <div
            className="text-[#212227] text-lg leading-relaxed mb-6"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          <h3 className="text-[#212227] text-2xl font-semibold mb-3">
            {name}
          </h3>

          <div className="flex gap-1 mb-10">
            {Array.from({ length: rating }).map((_, i) => (
              <FaStar key={i} className="text-[#FFCB2B] text-xl" />
            ))}
          </div>

          {/* ---------- Navigation ---------- */}
          <div className="flex gap-4">
            <button
              onClick={handlePrev}
              className="w-10 h-10 rounded-full bg-[#E2E8F0] flex items-center justify-center hover:bg-[#4177BC] hover:text-white transition-all"
            >
              <IoIosArrowBack className="text-xl" />
            </button>

            <button
              onClick={handleNext}
              className="w-10 h-10 rounded-full bg-[#E2E8F0] flex items-center justify-center hover:bg-[#4177BC] hover:text-white transition-all"
            >
              <IoIosArrowForward className="text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
